<template>
  <div class="view-invites-root">
    <MainNavbar />
    <div class="view-invites-page">
    
    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    
    <!-- Page Header -->
    <div class="page-header">
      <h1><i class="fa-solid fa-envelope-open-text"></i> Team Invites</h1>
      <p>View and manage your team invitations</p>
    </div>

    <!-- Main Content -->
    <div class="invites-content">
      <!-- Filter Tabs -->
      <div class="filter-tabs card slide-in">
        <button
          v-for="filter in inviteFilters"
          :key="filter.id"
          class="filter-tab"
          :class="{ active: statusFilter === filter.id }"
          @click="statusFilter = filter.id"
        >
          <i :class="filter.icon"></i>
          <span>{{ filter.label }}</span>
          <span class="filter-count" v-if="filter.count > 0">{{ filter.count }}</span>
        </button>
      </div>

      <!-- Invites List -->
      <div class="invites-list">
        <transition-group name="invite-list" tag="div">
          <div
            v-for="invite in filteredInvites"
            :key="invite.invite_id"
            class="invite-card card slide-in"
            :class="invite.status"
          >
            <div class="invite-header">
              <div class="team-info">
                <div class="team-avatar">
                  <i class="fa-solid fa-users"></i>
                </div>
                <div class="team-details">
                  <h3>{{ invite.team_name || 'Unknown Team' }}</h3>
                  <div class="team-meta">
                    <span class="meta-item">
                      <i class="fa-solid fa-user"></i>
                      From: {{ invite.sender_name || invite.sender_email || 'Unknown' }}
                    </span>
                    <span class="meta-item">
                      <i class="fa-solid fa-calendar"></i>
                      {{ formatDate(invite.created_at) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="invite-status-badge" :class="invite.status">
                <i :class="getStatusIcon(invite.status)"></i>
                {{ capitalizeStatus(invite.status) }}
              </div>
            </div>

            <div class="invite-body">
              <div class="invite-details">
                <div class="detail-row">
                  <i class="fa-solid fa-key"></i>
                  <span><strong>Invite Code:</strong> {{ invite.invite_code }}</span>
                </div>
                <div class="detail-row" v-if="invite.expires_at">
                  <i class="fa-solid fa-clock"></i>
                  <span>
                    <strong>Expires:</strong> 
                    {{ formatDate(invite.expires_at) }}
                    <span v-if="isExpiringSoon(invite.expires_at)" class="expiring-soon">
                      (Expiring soon)
                    </span>
                  </span>
                </div>
                <div class="detail-row" v-if="invite.responded_at">
                  <i class="fa-solid fa-check-double"></i>
                  <span><strong>Responded:</strong> {{ formatDate(invite.responded_at) }}</span>
                </div>
              </div>

              <!-- Player Stats (if already a member) -->
              <div v-if="invite.player_stats" class="player-stats">
                <h4><i class="fa-solid fa-chart-bar"></i> Player Stats</h4>
                <div class="stats-grid">
                  <div class="stat-item">
                    <i class="fa-solid fa-futbol"></i>
                    <span>{{ invite.player_stats.goals || 0 }} Goals</span>
                  </div>
                  <div class="stat-item">
                    <i class="fa-solid fa-handshake"></i>
                    <span>{{ invite.player_stats.assists || 0 }} Assists</span>
                  </div>
                  <div class="stat-item">
                    <i class="fa-solid fa-shield"></i>
                    <span>{{ invite.player_stats.clean_sheets || 0 }} Clean Sheets</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="invite-actions" v-if="invite.status === 'pending'">
              <button
                class="btn btn-success"
                @click="acceptInvite(invite.invite_id)"
                :disabled="processingInvite === invite.invite_id"
              >
                <i class="fa-solid" :class="processingInvite === invite.invite_id ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
                {{ processingInvite === invite.invite_id ? 'Accepting...' : 'Accept Invite' }}
              </button>
              <button
                class="btn btn-danger"
                @click="rejectInvite(invite.invite_id)"
                :disabled="processingInvite === invite.invite_id"
              >
                <i class="fa-solid" :class="processingInvite === invite.invite_id ? 'fa-spinner fa-spin' : 'fa-times-circle'"></i>
                {{ processingInvite === invite.invite_id ? 'Rejecting...' : 'Reject Invite' }}
              </button>
            </div>

            <!-- Status Message -->
            <div v-else class="invite-status-message" :class="invite.status">
              <i :class="getStatusIcon(invite.status)"></i>
              <span>{{ getStatusMessage(invite.status) }}</span>
            </div>
          </div>
        </transition-group>

        <!-- Empty State -->
        <div v-if="filteredInvites.length === 0 && !loading" class="empty-state">
          <div class="empty-icon">
            <i class="fa-solid fa-envelope-open"></i>
          </div>
          <h3>No Invites Found</h3>
          <p>
            {{ statusFilter !== 'all' 
              ? `No ${statusFilter} invites found` 
              : 'You have no team invitations at the moment' }}
          </p>
          <button class="btn btn-primary" @click="refreshInvites">
            <i class="fa-solid fa-refresh"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>
    </div>
    <ThirdpartyFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: 'ViewInvites',
  mixins: [toastMixin, globalLoadingMixin],
  components: { 
    MainNavbar,
    ThirdpartyFooter,
    ToastContainer
  },
  
  data() {
    return {
      loading: false,
      loadingMessage: 'Loading invites...',
      processingInvite: null,
      
      // Toast notifications
      toastId: 0,
      
      // Invites data
      invites: [],
      statusFilter: 'all',
    };
  },
  
  computed: {
    filteredInvites() {
      if (this.statusFilter === 'all') {
        return this.invites;
      }
      return this.invites.filter(invite => invite.status === this.statusFilter);
    },
    
    inviteFilters() {
      return [
        { 
          id: 'all', 
          label: 'All', 
          icon: 'fa-solid fa-list',
          count: this.invites.length
        },
        { 
          id: 'pending', 
          label: 'Pending', 
          icon: 'fa-solid fa-clock',
          count: this.invites.filter(i => i.status === 'pending').length
        },
        { 
          id: 'accepted', 
          label: 'Accepted', 
          icon: 'fa-solid fa-check-circle',
          count: this.invites.filter(i => i.status === 'accepted').length
        },
        { 
          id: 'declined', 
          label: 'Declined', 
          icon: 'fa-solid fa-times-circle',
          count: this.invites.filter(i => i.status === 'declined').length
        },
        { 
          id: 'expired', 
          label: 'Expired', 
          icon: 'fa-solid fa-exclamation-triangle',
          count: this.invites.filter(i => i.status === 'expired').length
        }
      ];
    }
  },
  
  async mounted() {
    await this.loadInvites();
  },
  
  methods: {
    // ========== HELPER METHODS ==========
    async getAuthToken() {
      return localStorage.getItem('access_token') || 
             sessionStorage.getItem('access_token');
    },
    
    // ========== API METHODS ==========
    async loadInvites() {
      this.loading = true;
      this.loadingMessage = 'Loading invites...';
      
      try {
        const token = await this.getAuthToken();
        if (!token) {
          this.showToast('error', 'Authentication required. Redirecting...');
          setTimeout(() => {
            this.$router.push('/ipromiseafrica/sign-in');
          }, 1500);
          return;
        }
        
        const response = await fetch(buildApiUrl('/api/teams/invites/received'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session expired. Please login again.');
            setTimeout(() => {
              this.$router.push('/ipromiseafrica/sign-in');
            }, 1500);
            return;
          }
          
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to load invites: ${response.status}`);
        }
        
        const data = await response.json();
        this.invites = data.invites || [];
        
        if (this.invites.length === 0) {
          this.showToast('info', 'No invites found');
        }
      } catch (error) {
        console.error('Error loading invites:', error);
        this.showToast('error', error.message || 'Failed to load invites');
      } finally {
        this.loading = false;
      }
    },
    
    async acceptInvite(inviteId) {
      this.processingInvite = inviteId;
      
      try {
        const token = await this.getAuthToken();
        if (!token) {
          this.showToast('error', 'Authentication required');
          return;
        }
        
        const response = await fetch(buildApiUrl(`/api/teams/invites/${inviteId}/accept`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session expired. Please login again.');
            setTimeout(() => {
              this.$router.push('/ipromiseafrica/sign-in');
            }, 1500);
            return;
          }
          
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to accept invite: ${response.status}`);
        }
        
        const data = await response.json();
        this.showToast('success', data.message || 'Invite accepted successfully!');
        
        // Update the invite in the list
        const inviteIndex = this.invites.findIndex(i => i.invite_id === inviteId);
        if (inviteIndex > -1) {
          this.invites[inviteIndex].status = 'accepted';
          this.invites[inviteIndex].responded_at = new Date().toISOString();
        }
        
        // Refresh invites after a short delay
        setTimeout(() => {
          this.loadInvites();
        }, 1000);
      } catch (error) {
        console.error('Error accepting invite:', error);
        this.showToast('error', error.message || 'Failed to accept invite');
      } finally {
        this.processingInvite = null;
      }
    },
    
    async rejectInvite(inviteId) {
      if (!confirm('Are you sure you want to reject this invite?')) {
        return;
      }
      
      this.processingInvite = inviteId;
      
      try {
        const token = await this.getAuthToken();
        if (!token) {
          this.showToast('error', 'Authentication required');
          return;
        }
        
        const response = await fetch(buildApiUrl(`/api/teams/invites/${inviteId}/reject`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast('error', 'Session expired. Please login again.');
            setTimeout(() => {
              this.$router.push('/ipromiseafrica/sign-in');
            }, 1500);
            return;
          }
          
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to reject invite: ${response.status}`);
        }
        
        const data = await response.json();
        this.showToast('success', data.message || 'Invite rejected');
        
        // Update the invite in the list
        const inviteIndex = this.invites.findIndex(i => i.invite_id === inviteId);
        if (inviteIndex > -1) {
          this.invites[inviteIndex].status = 'declined';
          this.invites[inviteIndex].responded_at = new Date().toISOString();
        }
        
        // Refresh invites after a short delay
        setTimeout(() => {
          this.loadInvites();
        }, 1000);
      } catch (error) {
        console.error('Error rejecting invite:', error);
        this.showToast('error', error.message || 'Failed to reject invite');
      } finally {
        this.processingInvite = null;
      }
    },
    
    async refreshInvites() {
      await this.loadInvites();
    },
    
    // ========== FORMATTING METHODS ==========
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    isExpiringSoon(expiresAt) {
      if (!expiresAt) return false;
      const expires = new Date(expiresAt);
      const now = new Date();
      const diffHours = (expires - now) / (1000 * 60 * 60);
      return diffHours > 0 && diffHours < 24; // Expiring within 24 hours
    },
    
    capitalizeStatus(status) {
      if (!status) return '';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
    
    getStatusIcon(status) {
      const icons = {
        pending: 'fa-solid fa-clock',
        accepted: 'fa-solid fa-check-circle',
        declined: 'fa-solid fa-times-circle',
        expired: 'fa-solid fa-exclamation-triangle',
        cancelled: 'fa-solid fa-ban'
      };
      return icons[status] || 'fa-solid fa-question-circle';
    },
    
    getStatusMessage(status) {
      const messages = {
        pending: 'Waiting for your response',
        accepted: 'You have accepted this invite',
        declined: 'You have declined this invite',
        expired: 'This invite has expired',
        cancelled: 'This invite has been cancelled'
      };
      return messages[status] || 'Unknown status';
    }
  }
};
</script>

<style scoped>
.view-invites-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Flush to main content: same as SignUp.vue */
.view-invites-root :deep(.thirdparty-footer) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.view-invites-page {
  flex: 1;
  min-height: 0;
  background: linear-gradient(135deg, var(--background-color1, #f5f7fa) 0%, var(--background-color2, #c3cfe2) 100%);
  padding-top: 80px;
  padding-bottom: 2rem;
}

.page-header {
  text-align: center;
  padding: 2rem 1rem;
  margin-top: 1.25rem;
  color: var(--text-color1, #1a1a1a);
}

.page-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.page-header h1 i {
  color: var(--border-color-hover, #3b82f6);
}

.page-header p {
  font-size: 1.125rem;
  color: var(--text-color2, #6b7280);
  margin: 0;
}

.invites-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Filter Tabs */
.filter-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  background: var(--background-color2, #ffffff);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-tab {
  padding: 0.75rem 1.25rem;
  border: 1px solid var(--background-color3, #e5e7eb);
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color2, #6b7280);
}

.filter-tab:hover {
  background: var(--background-color3, #f3f4f6);
  border-color: var(--border-color-hover, #3b82f6);
  color: var(--text-color1, #1a1a1a);
}

.filter-tab.active {
  background: var(--border-color-hover, #3b82f6);
  border-color: var(--border-color-hover, #3b82f6);
  color: var(--text-color3);
}

.filter-tab.active i,
.filter-tab.active span {
  color: var(--text-color3);
}

.filter-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.filter-tab.active .filter-count {
  background: rgba(0, 0, 0, 0.12);
  color: var(--text-color3);
}

/* Invites List */
.invites-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invite-card {
  background: var(--background-color2, #ffffff);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--background-color3, #e5e7eb);
}

.invite-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.invite-card.pending {
  border-left: 4px solid #f59e0b;
}

.invite-card.accepted {
  border-left: 4px solid #10b981;
}

.invite-card.declined {
  border-left: 4px solid #ef4444;
}

.invite-card.expired {
  border-left: 4px solid #6b7280;
}

.invite-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.team-info {
  display: flex;
  gap: 1rem;
  flex: 1;
}

.team-avatar {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--background-color2, #667eea), var(--background-color1, #764ba2));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.team-details h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  color: var(--text-color1, #1a1a1a);
}

.team-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-color2, #6b7280);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.meta-item i {
  font-size: 0.75rem;
}

.invite-status-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.invite-status-badge.pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.invite-status-badge.accepted {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.invite-status-badge.declined {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.invite-status-badge.expired {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.invite-body {
  margin-bottom: 1.5rem;
}

.invite-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--text-color2, #6b7280);
}

.detail-row i {
  width: 20px;
  text-align: center;
  color: var(--border-color-hover, #3b82f6);
}

.detail-row strong {
  color: var(--text-color1, #1a1a1a);
  margin-right: 0.5rem;
}

.expiring-soon {
  color: #f59e0b;
  font-weight: 600;
}

.player-stats {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--background-color3, #e5e7eb);
}

.player-stats h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
  color: var(--text-color1, #1a1a1a);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--background-color3, #f3f4f6);
  border-radius: 8px;
  font-size: 0.875rem;
  color: var(--text-color1, #1a1a1a);
}

.stat-item i {
  color: var(--border-color-hover, #3b82f6);
}

.invite-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--background-color3, #e5e7eb);
}

.invite-actions .btn {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-success {
  background: #10b981;
  color: #ffffff;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-danger {
  background: #ef4444;
  color: #ffffff;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.invite-status-message {
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 1rem;
}

.invite-status-message.accepted {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.invite-status-message.declined {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.invite-status-message.expired {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: var(--background-color2, #ffffff);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  font-size: 4rem;
  color: var(--background-color3, #9ca3af);
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  color: var(--text-color1, #1a1a1a);
}

.empty-state p {
  font-size: 1rem;
  color: var(--text-color2, #6b7280);
  margin: 0 0 1.5rem;
}

.empty-state .btn {
  padding: 0.75rem 2rem;
  background: var(--border-color-hover, #3b82f6);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.empty-state .btn:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Animations */
.slide-in {
  animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invite-list-enter-active,
.invite-list-leave-active {
  transition: all 0.3s ease;
}

.invite-list-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.invite-list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.invite-list-move {
  transition: transform 0.3s ease;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header h1 {
    font-size: 2rem;
  }
  
  .invite-header {
    flex-direction: column;
  }
  
  .invite-status-badge {
    align-self: flex-start;
  }
  
  .invite-actions {
    flex-direction: column;
  }
  
  .team-meta {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>
